import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { commonSpacesAPI } from '../../services/api';
import { SUCCESS_MESSAGES } from '../../utils/constants';
import { FiPlus, FiSearch, FiMapPin } from 'react-icons/fi';
import { Skeleton, SkeletonTable } from '../../design-system';
import CommonSpaceForm from '../../components/common_spaces/CommonSpaceForm';
import CommonSpaceTable from '../../components/common_spaces/CommonSpaceTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const CommonSpacesManagement = () => {
  const { addNotification } = useApp();
  const [commonSpaces, setCommonSpaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSpaceForm, setShowSpaceForm] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCommonSpaces();
  }, []);

  const loadCommonSpaces = async () => {
    try {
      setIsLoading(true);
      const spacesData = await commonSpacesAPI.getAll();
      setCommonSpaces(spacesData);
    } catch (error) {
      console.error('Error loading common spaces:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los espacios comunes'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSpace = () => {
    setSelectedSpace(null);
    setShowSpaceForm(true);
  };

  const handleEditSpace = (space) => {
    setSelectedSpace(space);
    setShowSpaceForm(true);
  };

  const handleDeleteSpace = (space) => {
    setDeleteConfirm(space);
  };

  const confirmDeleteSpace = async () => {
    try {
      await commonSpacesAPI.delete(deleteConfirm.id);
      setCommonSpaces(prevSpaces => prevSpaces.filter(s => s.id !== deleteConfirm.id));
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: 'Espacio común eliminado exitosamente'
      });
    } catch (error) {
      console.error('Error deleting common space:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar el espacio común'
      });
    }
  };

  const handleSpaceSaved = async (spaceData) => {
    try {
      if (selectedSpace) {
        // Update existing space
        const updatedSpace = await commonSpacesAPI.update(selectedSpace.id, spaceData);
        setCommonSpaces(prevSpaces =>
          prevSpaces.map(s => (s.id === selectedSpace.id ? updatedSpace : s))
        );
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.COMMON_SPACE_UPDATED
        });
      } else {
        // Create new space
        const newSpace = await commonSpacesAPI.create(spaceData);
        setCommonSpaces(prevSpaces => [...prevSpaces, newSpace]);
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.COMMON_SPACE_CREATED
        });
      }

      setShowSpaceForm(false);
      setSelectedSpace(null);
    } catch (error) {
      console.error('Error saving common space:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar el espacio común'
      });
    }
  };

  const getFilteredSpaces = () => {
    return commonSpaces.filter(space => {
      const matchesSearch = !searchTerm ||
        space.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    }).sort((a, b) => {
      // Sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  };

  const getSpaceStats = () => {
    const total = commonSpaces.length;
    const active = commonSpaces.filter(s => s.is_active).length;
    const avgCleaningTime = commonSpaces.length > 0
      ? Math.round(commonSpaces.reduce((sum, s) => sum + s.cleaning_time_minutes, 0) / commonSpaces.length)
      : 0;

    return { total, active, avgCleaningTime };
  };

  const filteredSpaces = getFilteredSpaces();
  const stats = getSpaceStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestión de Espacios Comunes
        </h1>
      </div>

      {/* Stats with Skeleton */}
      {isLoading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Skeleton variant="circle" width="48px" height="48px" />
                <div className="ml-4 flex-1 space-y-2">
                  <Skeleton variant="text" width="60%" height="14px" />
                  <Skeleton variant="text" width="40%" height="24px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-blue-100">
                <FiMapPin className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Espacios</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-green-100">
                <span className="text-green-600 font-bold text-xl">✓</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Espacios Activos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-3 bg-purple-100">
                <span className="text-purple-600 font-bold text-xl">⏱</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tiempo Promedio</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.avgCleaningTime} min</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left side: Search */}
          <div className="flex items-center gap-3 flex-1">
            <div className="relative max-w-md flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-9 w-full text-sm"
              />
            </div>
          </div>

          {/* Right side: New space button */}
          <button
            onClick={handleCreateSpace}
            className="btn btn-primary flex items-center space-x-2"
          >
            <FiPlus className="h-4 w-4" />
            <span>Nuevo Espacio</span>
          </button>
        </div>
      </div>

      {/* Spaces Table with Skeleton */}
      {isLoading ? (
        <SkeletonTable rows={6} columns={4} showActions />
      ) : (
        <CommonSpaceTable
          commonSpaces={filteredSpaces}
          onEdit={handleEditSpace}
          onDelete={handleDeleteSpace}
        />
      )}

      {/* Space Form Modal */}
      {showSpaceForm && (
        <CommonSpaceForm
          commonSpace={selectedSpace}
          existingSpaces={commonSpaces}
          onSave={handleSpaceSaved}
          onClose={() => {
            setShowSpaceForm(false);
            setSelectedSpace(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Espacio Común"
          message={`¿Estás seguro de que deseas eliminar el espacio "${deleteConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteSpace}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default CommonSpacesManagement;
